import { EMBARGO_EXTERNAL_ATTR, SORT_DESC_DIRECTION } from '@yme/common/constants';
import { FlParams } from '@yme/utils/bloomreach';
import { BLOOMREACH_ANALYTIC_KEYS_LIST, BLOOMREACH_ANALYTICS_DISCOUNTED_KEY, BLOOMREACH_QUERY_ANALYTIC_KEYS_LIST, BLOOMREACH_QUERY_ANALYTIC_SORT_MAPPING, SearchSortingValues, } from '@yme/utils/bloomreach/constants';
import { getParamsFromPDPBloomreachLink } from '@yme/utils/bloomreach/helpers';
import { getProductURL } from '@yme/utils/product';
import { DEFAULT_VALUE, DIGITAL_DATA_MANUFACTURER_NAME, EMPTY_BLOOMREACH_CATEGORIES_RESULT, GENERIC_CLICK_STATE, GUEST_USER_NAME, STATE_FOR_PRODUCT_OPTIONS_CLICK, } from './constants';
export function normalizeString(str, defaultValue = 'unspecified') {
    // replace space and hypern with underscore
    return str?.toLowerCase().replace(/[\s-]/g, '_') || defaultValue;
}
export function parseFilters(facets) {
    return facets
        .split('|')
        .map((el) => {
        const [key = '', rawWal = ''] = el.split(':');
        const opts = rawWal
            .split(',')
            .map((el) => el.replaceAll('"', '').trim())
            .filter((el) => el.length);
        return [key.trim().split('.').pop(), opts];
    })
        .filter((el) => el[0]?.length);
}
export function parseSort(sort) {
    return sort
        .split('|')
        .map((el) => {
        const fieldParts = el.trim().split('.');
        const direction = fieldParts.pop();
        const shortField = fieldParts.pop();
        return [shortField, [direction]];
    })
        .filter((el) => el[0]?.length && el[1]?.length);
}
export const parseBloomreachSort = (sort) => {
    const commonSortValue = {
        sortField: FlParams.SKU,
        sortDirection: SORT_DESC_DIRECTION,
    };
    let activeSortValue;
    if (sort === SearchSortingValues.SORT_DEFAULT) {
        const defaultSort = SearchSortingValues.SORT_RECOMMENDED.split(' ');
        activeSortValue = {
            sortField: defaultSort[0],
            sortDirection: defaultSort[1],
        };
    }
    if (sort === SearchSortingValues.SORT_PRICE_ASC || sort === SearchSortingValues.SORT_PRICE_DESC) {
        const priceSortSplit = sort.split(' ');
        const priceSortDirection = priceSortSplit[priceSortSplit.length - 1];
        activeSortValue = {
            sortField: FlParams.PRICE,
            sortDirection: priceSortDirection,
        };
    }
    if (sort === SearchSortingValues.SORT_DESC || sort === SearchSortingValues.SORT_ASC) {
        const dateSortSplit = sort.split(' ');
        const dateSortDirection = dateSortSplit[dateSortSplit.length - 1];
        activeSortValue = {
            sortField: EMBARGO_EXTERNAL_ATTR,
            sortDirection: dateSortDirection,
        };
    }
    return [activeSortValue, commonSortValue];
};
export function cursorToPagination(input) {
    const { from, till, total } = input;
    const resultCount = till - from + 1;
    const pagesCount = Math.ceil(total / resultCount);
    const currentPage = Math.ceil(from / resultCount);
    return {
        resultCount,
        pagesCount,
        currentPage,
    };
}
export function normalizeDate(date) {
    return date.toISOString().split('T')[0].replace(/-/g, '');
}
function isCoordinates(location) {
    const [rawLat = '', rawLan = '', tail = ''] = location.split(',');
    if (tail.length) {
        return false;
    }
    const lat = parseFloat(rawLat.trim());
    const lan = parseFloat(rawLan.trim());
    return !isNaN(lat) && !isNaN(lan);
}
export function normalizeLocation(location) {
    return isCoordinates(location) ? 'customer_location' : location.toLowerCase();
}
export function emptyDealerLocator() {
    return {
        id: '',
        name: '',
        country: '',
        location: '',
        category: '',
        services: [],
        contact: '',
    };
}
export function extractSlug(rawPath) {
    let url = rawPath.split('?')[0];
    url = url.split('#')[0];
    const segments = url.split('/').filter(Boolean);
    return segments.pop() || '';
}
export function configuratorParamsToEventInfo(configuratorData, configuratorUrl) {
    return {
        skuCode: normalizeString(configuratorData?.sku ?? ''),
        id: normalizeString(extractSlug(configuratorUrl ?? '')),
    };
}
export function attrValue(variant, attrName) {
    if (!variant)
        return undefined;
    return variant.attributes.find((attr) => attr.name === attrName)?.value;
}
export function isPDPPage() {
    const dd = getDigitalData();
    return [
        'accessories-product-details-page-template',
        'product-details-page-template',
        'clothing-and-merchandise-product-details-page-template',
    ].includes(dd?.data?.page?.category);
}
export function isInsideClickEventFromProductOptions() {
    if (!isPDPPage()) {
        return false;
    }
    return STATE_FOR_PRODUCT_OPTIONS_CLICK.isInsideClick;
}
export function isInsideGenericClickEvent() {
    return GENERIC_CLICK_STATE.isInsideClick;
}
export function captureEventToState(state, delay = 0) {
    state.isInsideClick = true;
    clearTimeout(state.timeoutId);
    cancelAnimationFrame(state.animationFrameId);
    if (delay > 0) {
        state.timeoutId = setTimeout(() => {
            state.animationFrameId = requestAnimationFrame(() => {
                state.isInsideClick = false;
            });
        }, delay);
    }
    else {
        state.animationFrameId = requestAnimationFrame(() => {
            state.isInsideClick = false;
        });
    }
}
export function closestDataTestIdFromTarget(e) {
    const target = e.closest('[data-testid]');
    return target?.dataset?.testid;
}
export function categoryNameFromKey(key) {
    if (!key)
        return '';
    return normalizeString(key.slice(0, key.lastIndexOf('-')));
}
export function categoriesFromProduct(product) {
    const sourceVariant = product.variants.find((v) => attrValue(v, 'accessoryTypeEn'));
    const publishedCategory = product.categories.find((c) => c.published);
    const parentCategory = publishedCategory?.parent;
    const rootCategory = parentCategory?.parent;
    const categoryName = categoryNameFromKey(publishedCategory?.key ?? '');
    const parentCategoryName = categoryNameFromKey(parentCategory?.key ?? '');
    const rootCategoryName = categoryNameFromKey(rootCategory?.key ?? '');
    const attr = normalizeString(attrValue(sourceVariant, 'accessoryTypeEn'), '');
    if (attr) {
        return {
            categoryName: attr,
            parentCategoryName: '',
            rootCategoryName: '',
            publishedCategory,
        };
    }
    return {
        categoryName: normalizeString(categoryName, ''),
        parentCategoryName: normalizeString(parentCategoryName, ''),
        rootCategoryName: normalizeString(rootCategoryName, ''),
        publishedCategory,
    };
}
export const getCategoriesFromBloomreachProduct = (categoriesString, unitType) => {
    if (!categoriesString) {
        return EMPTY_BLOOMREACH_CATEGORIES_RESULT;
    }
    const categories = JSON.parse(categoriesString);
    if (unitType) {
        return {
            categoryName: unitType,
            parentCategoryName: '',
            rootCategoryName: '',
            id: categories?.id,
        };
    }
    return {
        categoryName: normalizeString(categories?.name, ''),
        parentCategoryName: normalizeString(categories?.parent_name, ''),
        rootCategoryName: normalizeString(categories?.root_name, ''),
        id: categories?.id,
    };
};
export const getBloomreachTranslate = (value, translateData) => {
    const translation = translateData.find((translateObject) => {
        return translateObject.localizedValue === value;
    });
    return translation?.enValue || translation?.localizedValue || value;
};
export const createBloomreachValuesArray = (value, translateData = null) => {
    if (translateData) {
        return value.split(',').map((item) => getBloomreachTranslate(item.trim(), translateData));
    }
    return value.split(',').map((item) => item.trim());
};
export const getBloomreachTranslatedValues = (values, translateData) => {
    return values.map((value) => {
        return getBloomreachTranslate(value, translateData);
    });
};
export const parseBloomreachFilters = (filters, genderTranslations, accessoryTypeTranslations, accessoryUsageTranslations, subcategoryTranslations) => {
    const transformedObject = {};
    let isDiscountedKeyExisted = false;
    for (const [key, value] of Object.entries(filters)) {
        if (value) {
            transformedObject[key] = createBloomreachValuesArray(value);
        }
    }
    const bloomreachFiters = Object.entries(transformedObject).map(([key, values]) => {
        if (key === BLOOMREACH_ANALYTIC_KEYS_LIST.is_discounted) {
            isDiscountedKeyExisted = true;
            return {
                filterField: BLOOMREACH_ANALYTICS_DISCOUNTED_KEY,
                filterValues: [`${Boolean(values[0])}`],
            };
        }
        if (key === BLOOMREACH_ANALYTIC_KEYS_LIST.subcategories) {
            const currentTranslate = getBloomreachTranslatedValues(values, subcategoryTranslations);
            return {
                filterField: key,
                filterValues: currentTranslate,
            };
        }
        if (key === BLOOMREACH_ANALYTIC_KEYS_LIST.gender) {
            const currentTranslate = getBloomreachTranslatedValues(values, genderTranslations);
            return {
                filterField: key,
                filterValues: currentTranslate,
            };
        }
        if (key === BLOOMREACH_ANALYTIC_KEYS_LIST.type) {
            const currentTranslate = getBloomreachTranslatedValues(values, accessoryTypeTranslations);
            return {
                filterField: BLOOMREACH_QUERY_ANALYTIC_KEYS_LIST[key],
                filterValues: currentTranslate,
            };
        }
        if (key === BLOOMREACH_ANALYTIC_KEYS_LIST.accessory_usage) {
            const currentTranslate = getBloomreachTranslatedValues(values, accessoryUsageTranslations);
            return {
                filterField: BLOOMREACH_QUERY_ANALYTIC_KEYS_LIST.accessory_usage,
                filterValues: currentTranslate,
            };
        }
        return {
            filterField: BLOOMREACH_QUERY_ANALYTIC_KEYS_LIST[key],
            filterValues: values,
        };
    });
    if (!isDiscountedKeyExisted) {
        bloomreachFiters.push({
            filterField: BLOOMREACH_ANALYTICS_DISCOUNTED_KEY,
            filterValues: ['false'],
        });
    }
    return bloomreachFiters;
};
export const parseBloomreachQueryParams = (queryParams, genderTranslations, accessoryTypeTranslations, accessoryUsageTranslations, subcategoryTranslations) => {
    return queryParams.reduce((acc, [key, value]) => {
        const queryKey = BLOOMREACH_QUERY_ANALYTIC_KEYS_LIST[key];
        if (queryKey === BLOOMREACH_QUERY_ANALYTIC_KEYS_LIST.is_discounted) {
            acc[queryKey] = [`${Boolean(value)}`];
            return acc;
        }
        if (queryKey === BLOOMREACH_QUERY_ANALYTIC_KEYS_LIST.sort) {
            acc[queryKey] = [BLOOMREACH_QUERY_ANALYTIC_SORT_MAPPING[value].replace(' ', '.')];
            return acc;
        }
        if (queryKey === BLOOMREACH_QUERY_ANALYTIC_KEYS_LIST.subcategories) {
            acc[queryKey] = createBloomreachValuesArray(value, subcategoryTranslations);
            return acc;
        }
        if (queryKey === BLOOMREACH_QUERY_ANALYTIC_KEYS_LIST.type) {
            acc[queryKey] = createBloomreachValuesArray(value, accessoryTypeTranslations);
            return acc;
        }
        if (queryKey === BLOOMREACH_QUERY_ANALYTIC_KEYS_LIST.gender) {
            acc[queryKey] = createBloomreachValuesArray(value, genderTranslations);
            return acc;
        }
        if (queryKey === BLOOMREACH_QUERY_ANALYTIC_KEYS_LIST.accessory_usage) {
            acc[queryKey] = createBloomreachValuesArray(value, accessoryUsageTranslations);
            return acc;
        }
        acc[queryKey] = createBloomreachValuesArray(value);
        return acc;
    }, {});
};
export function mapToAdobeDataLayerProduct(unit, pdpPageUrl) {
    const masterVariant = unit.variants.find((v) => v.master);
    const price = masterVariant?.prices[0] || {
        currencyCode: DEFAULT_VALUE,
        amount: DEFAULT_VALUE,
    };
    const { categoryName, publishedCategory, parentCategoryName, rootCategoryName } = categoriesFromProduct(unit);
    return {
        id: normalizeString(unit.slug) || DEFAULT_VALUE,
        skuCode: normalizeString(masterVariant?.sku ?? '') || DEFAULT_VALUE,
        name: unit.productNameEn || DEFAULT_VALUE,
        currency: price.currencyCode,
        unitPrice: price.amount ?? DEFAULT_VALUE,
        unitSalePrice: masterVariant?.prices[0]?.discount?.discountedPrice ?? price.amount ?? DEFAULT_VALUE,
        category: categoryName,
        parentCategory: parentCategoryName,
        rootCategory: rootCategoryName,
        categoryId: normalizeString(publishedCategory?.id ?? ''),
        url: getProductURL({
            path: pdpPageUrl,
            isInAuthor: false,
            slug: unit.slug,
        }),
        manufacturer: DIGITAL_DATA_MANUFACTURER_NAME,
        color: normalizeString(attrValue(masterVariant, 'colourName')),
        size: normalizeString(attrValue(masterVariant, 'size')),
    };
}
export function mapToAdobeDataLayerBloomreachProduct(unit) {
    const { slug } = getParamsFromPDPBloomreachLink(unit.url);
    const sku = unit?.sku ?? '';
    const masterVariant = unit?.variants?.find((variant) => variant.skuid === sku);
    const categories = getCategoriesFromBloomreachProduct(unit.analytics_categories, unit.type_en);
    const unitPrice = masterVariant?.price_variant?.length
        ? masterVariant?.price_variant[0]
        : DEFAULT_VALUE;
    const unitSalePrice = masterVariant?.sale_price_variant?.length && masterVariant?.sale_price_variant[0]
        ? masterVariant?.sale_price_variant[0]
        : unitPrice;
    return {
        id: normalizeString(slug) || DEFAULT_VALUE,
        skuCode: normalizeString(sku || DEFAULT_VALUE),
        name: unit.title || DEFAULT_VALUE,
        currency: unit.currency_code || DEFAULT_VALUE,
        unitPrice,
        unitSalePrice,
        category: categories.categoryName,
        parentCategory: categories.parentCategoryName,
        rootCategory: categories.rootCategoryName,
        categoryId: normalizeString(categories?.id ?? ''),
        url: unit.url,
        manufacturer: 'yamaha',
        color: normalizeString(masterVariant?.sku_color),
        size: normalizeString(masterVariant?.sku_size),
    };
}
export function entryToLineItem(entry, getPdpLinkByCategory, currencyCode) {
    const { categoryName, parentCategoryName, rootCategoryName, publishedCategory } = categoriesFromProduct(entry.product);
    const variant = entry.product.variants[0];
    const masterSkuCode = normalizeString(entry?.masterVariantSku || '');
    return {
        product: {
            id: normalizeString(entry.product.slug),
            skuCode: normalizeString(variant?.sku ?? ''),
            masterSkuCode,
            name: entry.product.productNameEn ?? entry.product.name,
            currency: currencyCode,
            unitPrice: variant?.prices[0]?.amount ?? DEFAULT_VALUE,
            unitSalePrice: variant?.prices[0]?.discount?.discountedPrice ??
                variant?.prices[0]?.amount ??
                DEFAULT_VALUE,
            category: categoryName,
            parentCategory: parentCategoryName,
            rootCategory: rootCategoryName,
            categoryId: publishedCategory?.id || DEFAULT_VALUE,
            url: getProductURL({
                path: getPdpLinkByCategory(entry.product.categories),
                isInAuthor: false,
                slug: entry.product.slug,
            }),
            manufacturer: DIGITAL_DATA_MANUFACTURER_NAME,
            color: normalizeString(attrValue(variant, 'colourName')),
            size: normalizeString(attrValue(variant, 'size')),
        },
        quantity: entry.quantity,
        subTotal: entry.totalPrice.amount,
    };
}
export function serializeCart(input, getPdpLinkByCategory, isLineItemsEmpty = false) {
    const discount = input?.totalDiscount?.amount ?? 0;
    const subTotal = input.entries.reduce((sum, entry) => sum + entry.totalPriceWithoutDiscount.amount, 0);
    const total = subTotal + (input.shippingInfo?.price?.amount ?? 0) - discount;
    const output = {
        currency: input.currencyCode,
        subTotal: parseFloat(subTotal.toFixed(2)),
        total: parseFloat(total.toFixed(2)),
        id: input.id,
        voucher: input.discountCodes?.[0]?.code ?? '',
        voucherDiscount: input.totalDiscount?.amount ?? 0.0,
        shippingMethod: normalizeString(input.shippingInfo?.name),
        shippingCost: input.shippingInfo?.price?.amount ?? 0,
        paymentMethod: '',
        tax: input.totalTax?.amount ?? 0,
        lineItems: isLineItemsEmpty
            ? null
            : input.entries.map((entry) => {
                return entryToLineItem(entry, getPdpLinkByCategory, input.currencyCode);
            }),
    };
    return output;
}
export function serializeProductData(pd, currentSku) {
    const { categoryName, parentCategoryName, rootCategoryName } = categoriesFromProduct(pd);
    const product = {
        id: normalizeString(pd.slug),
        skuCode: normalizeString((currentSku || pd.variants.find((v) => v.master)?.sku) ?? ''),
        name: pd.productNameEn ?? pd.name,
        category: categoryName,
        parentCategory: parentCategoryName,
        rootCategory: rootCategoryName,
        variants: pd.variants.map((v) => ({
            product: {
                name: pd.productNameEn ?? pd.name,
                skuCode: normalizeString(v.sku),
                category: categoryName,
                parentCategory: parentCategoryName,
                rootCategory: rootCategoryName,
            },
        })),
    };
    return product;
}
export function getWindow() {
    return window;
}
export function getDigitalData() {
    if (!('digitalData' in getWindow())) {
        getWindow().digitalData = {
            data: {
                user: {
                    profile: {
                        profileInfo: {
                            userName: GUEST_USER_NAME,
                        },
                    },
                },
                version: '1.0',
                wishlist: [],
                cart: {},
                transaction: {},
                listings: [],
                recommendation: {},
                product: {},
                configuration: {},
                dealerLocator: emptyDealerLocator(),
                newsEvent: {},
                experience: {},
            },
            event: '',
            eventInfo: {},
        };
    }
    return getWindow().digitalData;
}
export function adobeDataLayer() {
    return getWindow().adobeDataLayer;
}
